<template>
  <div class="phone-monitor">
    <el-tabs v-model="subTab" class="phone-sub-tabs">
      <!-- 仪表盘 -->
      <el-tab-pane label="仪表盘" name="dashboard">
        <div class="kpi-row">
          <div class="kpi-card">
            <div class="kpi-label">总号码数</div>
            <div class="kpi-value">{{ stats.total_phones || 0 }}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">卡组数</div>
            <div class="kpi-value accent">{{ stats.group_count || 0 }}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">需管付费</div>
            <div class="kpi-value">{{ stats.managed_phones || 0 }}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">不管付费</div>
            <div class="kpi-value">{{ stats.unmanaged_phones || 0 }}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">月费合计</div>
            <div class="kpi-value accent">¥{{ (stats.total_monthly_fee || 0).toFixed(0) }}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">今日提醒</div>
            <div class="kpi-value" :class="{ danger: (stats.alert_count || 0) > 0 }">
              {{ stats.alert_count || 0 }}
            </div>
          </div>
        </div>

        <section class="mp-panel">
          <div class="mp-panel-header">
            <span class="mp-panel-title">今日提醒</span>
            <el-button type="primary" size="small" :loading="checking" @click="runCheck(false)">
              立即检查
            </el-button>
          </div>
          <div class="mp-panel-body">
            <div v-if="!alerts.length" class="empty-tip">今日无提醒，一切正常</div>
            <div v-for="(a, i) in alerts" :key="i" class="alert-item" :class="a.type">
              <div class="alert-type">{{ alertLabel(a.type) }} · {{ a.phone }}</div>
              <pre>{{ a.message }}</pre>
            </div>
          </div>
        </section>

        <section class="mp-panel">
          <div class="mp-panel-header">
            <span class="mp-panel-title">余额不足号码</span>
          </div>
          <div class="mp-panel-body">
            <el-table :data="lowBalancePhones" size="small" stripe empty-text="所有号码余额充足">
              <el-table-column prop="phone" label="号码" width="130" />
              <el-table-column prop="group_name" label="卡组" width="120" show-overflow-tooltip />
              <el-table-column prop="operator" label="运营商" width="100" />
              <el-table-column label="月费" width="80">
                <template #default="{ row }">
                  {{ row.monthly_fee > 0 ? row.monthly_fee + '元' : '—' }}
                </template>
              </el-table-column>
              <el-table-column label="余额" width="120">
                <template #default="{ row }">
                  <span class="text-danger">¥{{ displayBalance(row).toFixed(2) }}</span>
                  <span v-if="row.group_id" class="bal-hint">（共享）</span>
                </template>
              </el-table-column>
              <el-table-column label="状态" width="100">
                <template #default>
                  <el-tag type="danger" size="small" effect="plain">余额不足</el-tag>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </section>
      </el-tab-pane>

      <!-- 号码管理 -->
      <el-tab-pane label="号码管理" name="phones">
        <section class="mp-panel">
          <div class="mp-panel-header">
            <span class="mp-panel-title">号码列表</span>
            <div class="header-actions">
              <el-input
                v-model="search"
                clearable
                placeholder="搜索号码 / 机主 / 归属地 / 平台账号"
                style="width: 240px"
                size="small"
              />
              <el-button type="primary" size="small" @click="openEdit()">添加号码</el-button>
              <el-button size="small" @click="exportExcel" :loading="exporting">
                <el-icon style="margin-right:4px"><Download /></el-icon>导出Excel
              </el-button>
            </div>
          </div>
          <div class="mp-panel-body">
            <el-table :data="filteredPhones" stripe size="small" v-loading="loading" max-height="560">
              <el-table-column prop="phone" label="号码" width="120" fixed />
              <el-table-column prop="operator" label="运营商" width="100" />
              <el-table-column prop="location" label="归属地" width="100" />
              <el-table-column prop="account_type" label="账号类型" width="110" />
              <el-table-column prop="owner" label="机主" width="90" />
              <el-table-column label="卡组" width="120" show-overflow-tooltip>
                <template #default="{ row }">
                  {{ row.group_name || '—' }}
                </template>
              </el-table-column>
              <el-table-column label="卡类型" width="90">
                <template #default="{ row }">
                  <el-tag v-if="row.card_role && row.card_role !== 'standalone'" size="small" effect="plain">
                    {{ row.card_role_label || row.card_role }}
                  </el-tag>
                  <span v-else>独立</span>
                </template>
              </el-table-column>
              <el-table-column label="付费" width="70">
                <template #default="{ row }">
                  <el-tag :type="row.billing_managed ? 'success' : 'info'" size="small" effect="plain">
                    {{ row.billing_managed ? '管理' : '不管' }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="月费" width="70">
                <template #default="{ row }">
                  <span v-if="!row.billing_managed">—</span>
                  <span v-else>{{ row.monthly_fee > 0 ? `¥${row.monthly_fee}` : '—' }}</span>
                </template>
              </el-table-column>
              <el-table-column label="扣款日" width="110">
                <template #default="{ row }">
                  <template v-if="row.billing_managed">
                    <span>{{ row.deduction_str || '—' }}</span>
                    <el-tag
                      v-if="row.deduction_type === 'monthly'"
                      size="small"
                      type="primary"
                      effect="plain"
                      style="margin-left: 4px"
                    >月扣</el-tag>
                    <el-tag
                      v-else-if="row.deduction_type === 'daily'"
                      size="small"
                      type="warning"
                      effect="plain"
                      style="margin-left: 4px"
                    >日结</el-tag>
                  </template>
                  <span v-else>—</span>
                </template>
              </el-table-column>
              <el-table-column label="余额" width="110">
                <template #default="{ row }">
                  <span
                    :class="{ 'text-danger': displayBalance(row) < threshold }"
                  >
                    ¥{{ displayBalance(row).toFixed(2) }}
                  </span>
                  <span v-if="row.group_id" class="bal-hint">共享</span>
                </template>
              </el-table-column>
              <el-table-column
                v-for="plat in platformKeys"
                :key="plat"
                :label="plat"
                min-width="120"
                show-overflow-tooltip
              >
                <template #default="{ row }">
                  <span class="plat-cell" :style="platCellStyle(row, plat)">{{
                    row[`plat_${plat}`] || '—'
                  }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="remark" label="备注" width="90" />
              <el-table-column label="操作" width="120" fixed="right">
                <template #default="{ row }">
                  <el-button link type="primary" size="small" @click="openEdit(row)">编辑</el-button>
                  <el-button link type="danger" size="small" @click="remove(row)">删除</el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </section>
      </el-tab-pane>

      <!-- 卡组管理 -->
      <el-tab-pane label="卡组管理" name="groups">
        <section class="mp-panel">
          <div class="mp-panel-header">
            <span class="mp-panel-title">卡组列表</span>
            <el-button type="primary" size="small" @click="openGroupEdit()">新建卡组</el-button>
          </div>
          <div class="mp-panel-body">
            <el-table :data="groups" stripe size="small" v-loading="groupsLoading" empty-text="暂无卡组">
              <el-table-column prop="name" label="卡组名称" min-width="140" />
              <el-table-column prop="id" label="ID" width="140" show-overflow-tooltip />
              <el-table-column label="付费管理" width="90">
                <template #default="{ row }">
                  <el-tag :type="row.billing_managed ? 'success' : 'info'" size="small" effect="plain">
                    {{ row.billing_managed ? '管理' : '不管' }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="balance_phone" label="余额号码" width="120" />
              <el-table-column prop="member_count" label="成员数" width="70" />
              <el-table-column prop="note" label="备注" min-width="160" show-overflow-tooltip />
              <el-table-column label="成员" min-width="280">
                <template #default="{ row }">
                  <span v-for="m in row.members" :key="m.phone" class="member-chip">
                    {{ m.phone }}({{ m.role_label }})
                  </span>
                </template>
              </el-table-column>
              <el-table-column label="操作" width="140" fixed="right">
                <template #default="{ row }">
                  <el-button link type="primary" size="small" @click="openGroupEdit(row)">编辑</el-button>
                  <el-button link type="danger" size="small" @click="removeGroup(row)">删除</el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </section>
      </el-tab-pane>

      <!-- 提醒记录 -->
      <el-tab-pane label="提醒记录" name="alerts">
        <section class="mp-panel">
          <div class="mp-panel-header">
            <span class="mp-panel-title">提醒记录</span>
            <el-button size="small" @click="loadHistory">刷新</el-button>
          </div>
          <div class="mp-panel-body">
            <div v-if="!history.length" class="empty-tip">暂无提醒记录，点「立即检查」后会出现</div>
            <div v-for="(a, i) in historyDesc" :key="i" class="alert-item" :class="a.type">
              <div class="alert-type">{{ a.time }} · {{ a.phone }} · {{ alertLabel(a.type) }}</div>
              <pre>{{ a.message }}</pre>
            </div>
          </div>
        </section>
      </el-tab-pane>

      <!-- 系统设置 -->
      <el-tab-pane label="系统设置" name="settings">
        <section class="mp-panel">
          <div class="mp-panel-header">
            <span class="mp-panel-title">系统配置</span>
            <el-tag :type="dingtalkReady ? 'success' : 'info'" size="small" effect="plain">
              钉钉播报：{{ dingtalkReady ? '已开启' : (cfg.dingtalk_enabled ? '未选机器人' : '已关闭') }}
            </el-tag>
          </div>
          <div class="mp-panel-body">
            <el-form label-width="150px" size="default" style="max-width: 640px">
              <el-form-item label="钉钉播报">
                <el-switch
                  v-model="cfg.dingtalk_enabled"
                  active-text="开启"
                  inactive-text="关闭"
                  @change="saveCfg"
                />
                <span class="cfg-hint">关闭后检查只记本地，不会转发到钉钉群</span>
              </el-form-item>
              <el-form-item label="转发机器人">
                <div style="width: 100%">
                  <el-select
                    v-model="cfg.dingtalk_robot_id"
                    clearable
                    filterable
                    placeholder="选择钉钉群（与短信共用）"
                    style="width: 100%; max-width: 360px"
                    :loading="robotsLoading"
                    @change="saveCfg"
                  >
                    <el-option
                      v-for="r in robotsList"
                      :key="r.id"
                      :label="r.name + (r.enabled ? '' : ' (已禁用)')"
                      :value="r.id"
                      :disabled="!r.enabled"
                    />
                  </el-select>
                  <div class="cfg-hint" style="margin-left: 0; margin-top: 6px; display: block">
                    机器人在「短信网关 → 钉钉机器人」中维护，发送时按所选机器人转发
                  </div>
                </div>
              </el-form-item>
              <el-form-item label="检查时间">
                <el-input v-model="checkTimesText" placeholder="如 09:00, 18:00" />
              </el-form-item>
              <el-form-item label="余额报警阈值（元）">
                <el-input-number v-model="cfg.balance_alert_threshold" :min="0" :step="1" />
              </el-form-item>
              <el-form-item label="日结余额阈值（元）">
                <el-input-number v-model="cfg.daily_balance_threshold" :min="0" :step="1" />
              </el-form-item>
              <el-form-item label="扣款前提醒天数">
                <el-input v-model="remindDaysText" placeholder="如 3, 1, 0" />
              </el-form-item>
              <el-form-item label="月初汇总">
                <el-switch v-model="cfg.month_start_summary" />
              </el-form-item>
              <el-form-item>
                <el-button type="primary" :loading="savingCfg" @click="saveCfg">保存配置</el-button>
              </el-form-item>
            </el-form>
          </div>
        </section>

        <section class="mp-panel">
          <div class="mp-panel-header">
            <span class="mp-panel-title">操作</span>
          </div>
          <div class="mp-panel-body">
            <div class="header-actions">
              <el-button type="primary" :loading="checking" @click="runCheck(false)">
                立即检查（仅本地）
              </el-button>
              <el-button
                type="warning"
                :loading="checking"
                :disabled="!dingtalkReady"
                @click="runCheck(true)"
              >
                检查并推送钉钉
              </el-button>
              <el-button
                :loading="testing"
                :disabled="!dingtalkReady"
                @click="sendTest"
              >
                发送测试消息
              </el-button>
            </div>
            <p class="cfg-hint" style="margin-top: 12px">
              {{ dingtalkHint }}
            </p>
          </div>
        </section>
      </el-tab-pane>
    </el-tabs>

    <!-- 编辑弹窗 -->
    <el-dialog v-model="dialogVisible" :title="editingPhone ? '编辑号码' : '添加号码'" width="640px">
      <el-form :model="form" label-width="90px" size="default">
        <el-form-item label="手机号码">
          <el-input v-model="form.phone" :disabled="!!editingPhone" maxlength="11" />
        </el-form-item>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="运营商">
              <el-select v-model="form.operator" clearable style="width: 100%">
                <el-option label="移动" value="移动" />
                <el-option label="联通" value="联通" />
                <el-option label="电信" value="电信" />
                <el-option label="小米（虚商/联通网）" value="小米（虚商/联通网）" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="归属地">
              <el-input v-model="form.location" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="账号类型">
              <el-input v-model="form.account_type" placeholder="营销号 / 官方号 / 养成号…" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="机主">
              <el-input v-model="form.owner" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="月费">
              <el-input-number v-model="form.monthly_fee" :min="0" :step="1" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="余额">
              <el-input-number v-model="form.balance" :step="1" :precision="2" style="width: 100%" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="扣款日">
          <el-input v-model="form.deduction_str" placeholder="如：15日 / 2-6日 / 日结" />
        </el-form-item>
        <el-form-item label="联系人">
          <el-input v-model="form.contact" />
        </el-form-item>
        <el-divider content-position="left">平台账号</el-divider>
        <el-form-item v-for="plat in platformKeys" :key="plat" :label="plat">
          <el-input v-model="form[`plat_${plat}`]" :placeholder="`${plat} 账号名`" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="form.remark" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="save">保存</el-button>
      </template>
    </el-dialog>

    <!-- 卡组编辑弹窗 -->
    <el-dialog
      v-model="groupDialogVisible"
      :title="editingGroupId ? '编辑卡组' : '新建卡组'"
      width="680px"
    >
      <el-form :model="groupForm" label-width="100px" size="default">
        <el-form-item label="卡组名称" required>
          <el-input v-model="groupForm.name" placeholder="如：刘颖-遇次元" />
        </el-form-item>
        <el-form-item label="付费管理">
          <el-switch v-model="groupForm.billing_managed" active-text="管理" inactive-text="不管" />
          <span class="cfg-hint">关闭后组内号码不参与月费/扣款/余额提醒</span>
        </el-form-item>
        <el-form-item v-if="groupForm.billing_managed" label="余额号码">
          <el-select
            v-model="groupForm.balance_phone"
            clearable
            filterable
            placeholder="选组内主卡号码"
            style="width: 100%"
          >
            <el-option
              v-for="m in groupForm.member_list"
              :key="m.phone"
              :label="m.phone"
              :value="m.phone"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="groupForm.note" type="textarea" :rows="2" />
        </el-form-item>
        <el-divider content-position="left">成员</el-divider>
        <div v-for="(m, idx) in groupForm.member_list" :key="idx" class="member-row">
          <el-select
            v-model="m.phone"
            filterable
            placeholder="选择号码"
            style="flex: 1"
          >
            <el-option
              v-for="p in phones"
              :key="p.phone"
              :label="`${p.phone} ${p.owner || ''}`"
              :value="p.phone"
            />
          </el-select>
          <el-select v-model="m.role" style="width: 120px">
            <el-option label="主卡" value="main" />
            <el-option label="分卡" value="sub" />
            <el-option label="外部" value="external" />
            <el-option label="免管" value="exempt" />
          </el-select>
          <el-button link type="danger" @click="removeGroupMember(idx)">移除</el-button>
        </div>
        <el-button size="small" @click="addGroupMember">添加成员</el-button>
      </el-form>
      <template #footer>
        <el-button @click="groupDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="savingGroup" @click="saveGroup">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, computed, reactive, onMounted, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { phoneMonitorApi, smsApi } from '../api'

const PLATFORM_KEYS = ['小红书', '知乎', '百家号', '头条', '抖音', 'B站', '微博', '微博官方号']

const emptyForm = () => {
  const base = {
    phone: '',
    operator: '',
    location: '',
    account_type: '',
    owner: '',
    contact: '',
    monthly_fee: 0,
    balance: 0,
    deduction_str: '',
    platforms: '',
    remark: '',
    pin: '',
  }
  PLATFORM_KEYS.forEach((k) => {
    base[`plat_${k}`] = ''
  })
  return base
}

const emptyGroupForm = () => ({
  name: '',
  billing_managed: true,
  balance_phone: '',
  note: '',
  member_list: [{ phone: '', role: 'sub' }],
})

export default {
  name: 'PhoneMonitorPanel',
  setup() {
    const subTab = ref('dashboard')
    const loading = ref(false)
    const checking = ref(false)
    const testing = ref(false)
    const saving = ref(false)
    const savingCfg = ref(false)
    const savingGroup = ref(false)
    const exporting = ref(false)
    const groups = ref([])
    const groupsLoading = ref(false)
    const groupDialogVisible = ref(false)
    const editingGroupId = ref(null)
    const groupForm = ref(emptyGroupForm())
    const phones = ref([])
    const stats = ref({})
    const alerts = ref([])
    const history = ref([])
    const search = ref('')
    const dialogVisible = ref(false)
    const editingPhone = ref(null)
    const form = ref(emptyForm())
    const platformKeys = PLATFORM_KEYS
    const cfg = reactive({
      dingtalk_enabled: false,
      dingtalk_robot_id: null,
      check_times: ['09:00', '18:00'],
      balance_alert_threshold: 20,
      deduction_remind_days: [3, 1, 0],
      month_start_summary: true,
      daily_balance_threshold: 10,
      silent_phones: [],
    })
    const checkTimesText = ref('09:00, 18:00')
    const remindDaysText = ref('3, 1, 0')
    const robotsList = ref([])
    const robotsLoading = ref(false)

    const dingtalkReady = computed(
      () => !!cfg.dingtalk_enabled && !!cfg.dingtalk_robot_id,
    )
    const dingtalkHint = computed(() => {
      if (!cfg.dingtalk_enabled) {
        return '当前钉钉播报已关闭。关闭时「推送钉钉 / 测试消息」不可用。'
      }
      if (!cfg.dingtalk_robot_id) {
        return '已开启播报，请选择转发机器人（与短信共用钉钉机器人配置）。'
      }
      return '钉钉播报已开启，提醒将转发到所选机器人。'
    })

    const threshold = computed(() => Number(cfg.balance_alert_threshold) || 20)

    const filteredPhones = computed(() => {
      const q = (search.value || '').trim().toLowerCase()
      if (!q) return phones.value
      return phones.value.filter((p) => {
        const platText = PLATFORM_KEYS.map((k) => p[`plat_${k}`] || '').join(' ').toLowerCase()
        return (
          p.phone.includes(q) ||
          (p.operator || '').toLowerCase().includes(q) ||
          (p.location || '').toLowerCase().includes(q) ||
          (p.owner || '').toLowerCase().includes(q) ||
          (p.account_type || '').toLowerCase().includes(q) ||
          platText.includes(q)
        )
      })
    })

    const displayBalance = (row) => {
      if (!row) return 0
      if (row.group_id) return Number(row.shared_balance ?? row.balance ?? 0)
      return Number(row.balance || 0)
    }

    const platCellStyle = (row, plat) => {
      const hex = row?.cell_colors?.[plat]
      if (!hex) return {}
      const h = String(hex).replace(/^#/, '')
      if (!/^[0-9A-Fa-f]{6}$/.test(h)) return {}
      return {
        backgroundColor: `#${h}`,
        display: 'inline-block',
        padding: '2px 6px',
        borderRadius: '2px',
        lineHeight: '1.4',
        maxWidth: '100%',
      }
    }

    const lowBalancePhones = computed(() =>
      phones.value.filter((p) => {
        if (!p.billing_managed) return false
        const bal = displayBalance(p)
        return bal < threshold.value
      })
    )

    const historyDesc = computed(() => [...history.value].reverse())

    const alertLabel = (type) =>
      ({
        debt: '已欠费',
        insufficient: '不够下次扣费',
        deduction_day: '扣款当天',
        deduction_soon: '扣款临近',
        balance_low: '余额不足',
        daily_low: '日结不足',
      }[type] || type)

    async function loadGroups() {
      groupsLoading.value = true
      try {
        groups.value = (await phoneMonitorApi.getGroups()) || []
      } catch (e) {
        ElMessage.error(e.message || '加载卡组失败')
      } finally {
        groupsLoading.value = false
      }
    }

    async function loadPhonesAndStats() {
      loading.value = true
      try {
        const [p, s] = await Promise.all([
          phoneMonitorApi.getPhones(),
          phoneMonitorApi.getStats(),
        ])
        phones.value = p || []
        stats.value = s || {}
      } catch (e) {
        ElMessage.error(e.message || '加载失败')
      } finally {
        loading.value = false
      }
    }

    async function loadCfg() {
      try {
        const res = await phoneMonitorApi.getConfig()
        Object.assign(cfg, res || {})
        checkTimesText.value = (cfg.check_times || []).join(', ')
        remindDaysText.value = (cfg.deduction_remind_days || []).join(', ')
      } catch (e) {
        ElMessage.error(e.message || '加载配置失败')
      }
    }

    async function loadRobots() {
      robotsLoading.value = true
      try {
        const res = await smsApi.listRobots()
        robotsList.value = res?.items || []
        if (!Array.isArray(robotsList.value)) robotsList.value = []
      } catch (e) {
        robotsList.value = []
      } finally {
        robotsLoading.value = false
      }
    }

    async function loadHistory() {
      try {
        history.value = (await phoneMonitorApi.getAlertHistory(80)) || []
      } catch (e) {
        ElMessage.error(e.message || '加载提醒记录失败')
      }
    }

    async function runCheck(notify) {
      checking.value = true
      try {
        const res = await phoneMonitorApi.check(!!notify)
        alerts.value = res?.alerts || []
        await Promise.all([loadPhonesAndStats(), loadHistory()])
        if (notify) {
          if (res?.notified) ElMessage.success(`检查完成，已推送 ${res.count || 0} 条到钉钉`)
          else if (!res?.dingtalk_enabled) ElMessage.warning('钉钉已关闭，仅完成本地检查')
          else if (!res?.dingtalk_robot_id) ElMessage.warning('未选择转发机器人，仅完成本地检查')
          else ElMessage.success(`检查完成，${res?.count || 0} 条提醒（未推送）`)
        } else {
          ElMessage.success(`检查完成，${res?.count || 0} 条提醒（未推钉钉）`)
        }
      } catch (e) {
        ElMessage.error(e.message || '检查失败')
      } finally {
        checking.value = false
      }
    }

    async function sendTest() {
      testing.value = true
      try {
        const res = await phoneMonitorApi.testMessage()
        if (res?.ok) ElMessage.success('测试消息已发送')
        else ElMessage.error(res?.error || '发送失败')
      } catch (e) {
        ElMessage.error(e.message || '发送失败')
      } finally {
        testing.value = false
      }
    }

    async function saveCfg() {
      savingCfg.value = true
      try {
        const times = checkTimesText.value
          .split(/[,，\s]+/)
          .map((s) => s.trim().replace(/[：﹕]/g, ':'))
          .filter(Boolean)
        const days = remindDaysText.value
          .split(/[,，\s]+/)
          .map((s) => parseInt(s.trim(), 10))
          .filter((n) => !Number.isNaN(n))
        const res = await phoneMonitorApi.updateConfig({
          dingtalk_enabled: !!cfg.dingtalk_enabled,
          dingtalk_robot_id: cfg.dingtalk_robot_id || null,
          check_times: times,
          balance_alert_threshold: cfg.balance_alert_threshold,
          daily_balance_threshold: cfg.daily_balance_threshold,
          deduction_remind_days: days,
          month_start_summary: !!cfg.month_start_summary,
          silent_phones: cfg.silent_phones || [],
        })
        Object.assign(cfg, res || {})
        checkTimesText.value = (cfg.check_times || []).join(', ')
        remindDaysText.value = (cfg.deduction_remind_days || []).join(', ')
        if (!cfg.dingtalk_enabled) {
          ElMessage.success('配置已保存（钉钉已关闭）')
        } else if (!cfg.dingtalk_robot_id) {
          ElMessage.warning('配置已保存，请选择转发机器人后才能推送')
        } else {
          ElMessage.success('配置已保存（钉钉已开启）')
        }
      } catch (e) {
        ElMessage.error(e.message || '保存失败')
      } finally {
        savingCfg.value = false
      }
    }

    function openEdit(row) {
      if (row) {
        editingPhone.value = row.phone
        const next = emptyForm()
        Object.assign(next, {
          phone: row.phone,
          operator: row.operator || '',
          location: row.location || '',
          account_type: row.account_type || '',
          owner: row.owner || '',
          contact: row.contact || '',
          monthly_fee: row.monthly_fee || 0,
          balance: row.balance || 0,
          deduction_str: row.deduction_str || '',
          platforms: row.platforms || '',
          remark: row.remark || '',
          pin: row.pin || '',
        })
        PLATFORM_KEYS.forEach((k) => {
          next[`plat_${k}`] = row[`plat_${k}`] || ''
        })
        form.value = next
      } else {
        editingPhone.value = null
        form.value = emptyForm()
      }
      dialogVisible.value = true
    }

    async function save() {
      if (!form.value.phone || form.value.phone.length !== 11) {
        ElMessage.warning('请输入11位手机号')
        return
      }
      saving.value = true
      try {
        if (editingPhone.value) {
          await phoneMonitorApi.updatePhone(editingPhone.value, form.value)
          ElMessage.success('已更新')
        } else {
          await phoneMonitorApi.addPhone(form.value)
          ElMessage.success('已添加')
        }
        dialogVisible.value = false
        await loadPhonesAndStats()
      } catch (e) {
        ElMessage.error(e.message || '保存失败')
      } finally {
        saving.value = false
      }
    }

    function openGroupEdit(row) {
      if (row) {
        editingGroupId.value = row.id
        groupForm.value = {
          name: row.name || '',
          billing_managed: !!row.billing_managed,
          balance_phone: row.balance_phone || '',
          note: row.note || '',
          member_list: (row.members || []).map((m) => ({
            phone: m.phone,
            role: m.role || 'sub',
          })),
        }
        if (!groupForm.value.member_list.length) {
          groupForm.value.member_list = [{ phone: '', role: 'sub' }]
        }
      } else {
        editingGroupId.value = null
        groupForm.value = emptyGroupForm()
      }
      groupDialogVisible.value = true
    }

    function addGroupMember() {
      groupForm.value.member_list.push({ phone: '', role: 'sub' })
    }

    function removeGroupMember(idx) {
      groupForm.value.member_list.splice(idx, 1)
    }

    async function saveGroup() {
      const name = (groupForm.value.name || '').trim()
      if (!name) {
        ElMessage.warning('请填写卡组名称')
        return
      }
      const memberList = groupForm.value.member_list.filter((m) => m.phone)
      if (!memberList.length) {
        ElMessage.warning('请至少添加一个成员号码')
        return
      }
      const phonesSet = new Set(memberList.map((m) => m.phone))
      if (phonesSet.size !== memberList.length) {
        ElMessage.warning('成员号码不能重复')
        return
      }
      savingGroup.value = true
      try {
        const payload = {
          name,
          billing_managed: !!groupForm.value.billing_managed,
          balance_phone: groupForm.value.balance_phone || '',
          note: groupForm.value.note || '',
          member_list: memberList,
        }
        if (editingGroupId.value) {
          await phoneMonitorApi.updateGroup(editingGroupId.value, payload)
          ElMessage.success('卡组已更新')
        } else {
          await phoneMonitorApi.createGroup(payload)
          ElMessage.success('卡组已创建')
        }
        groupDialogVisible.value = false
        await Promise.all([loadGroups(), loadPhonesAndStats()])
      } catch (e) {
        ElMessage.error(e.message || '保存失败')
      } finally {
        savingGroup.value = false
      }
    }

    async function removeGroup(row) {
      try {
        await ElMessageBox.confirm(`确定删除卡组「${row.name}」？号码不会被删除`, '确认', {
          type: 'warning',
        })
        await phoneMonitorApi.deleteGroup(row.id)
        ElMessage.success('已删除')
        await Promise.all([loadGroups(), loadPhonesAndStats()])
      } catch (e) {
        if (e !== 'cancel') ElMessage.error(e.message || '删除失败')
      }
    }

    async function remove(row) {
      try {
        await ElMessageBox.confirm(`确定删除 ${row.phone}？`, '确认', { type: 'warning' })
        await phoneMonitorApi.deletePhone(row.phone)
        ElMessage.success('已删除')
        await loadPhonesAndStats()
      } catch (e) {
        if (e !== 'cancel') ElMessage.error(e.message || '删除失败')
      }
    }

    watch(subTab, (tab) => {
      if (tab === 'alerts') loadHistory()
      if (tab === 'settings') {
        loadCfg()
        loadRobots()
      }
      if (tab === 'groups') loadGroups()
    })

    async function exportExcel() {
      exporting.value = true
      try {
        const response = await fetch('/api/phone-monitor/phones/export')
        if (!response.ok) {
          const msg = await response.text().catch(() => '导出失败')
          ElMessage.error(msg)
          return
        }
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = ''
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
        ElMessage.success('导出成功')
      } catch (e) {
        ElMessage.error(e.message || '导出失败')
      } finally {
        exporting.value = false
      }
    }

    onMounted(async () => {
      await Promise.all([loadPhonesAndStats(), loadCfg(), loadGroups(), loadRobots()])
      // 仅本地检查，不推钉钉
      try {
        const res = await phoneMonitorApi.check(false)
        alerts.value = res?.alerts || []
        await loadHistory()
      } catch (_) {
        /* ignore */
      }
    })

    return {
      subTab,
      loading,
      checking,
      testing,
      saving,
      savingCfg,
      savingGroup,
      exporting,
      exportExcel,
      groups,
      groupsLoading,
      groupDialogVisible,
      editingGroupId,
      groupForm,
      displayBalance,
      platCellStyle,
      openGroupEdit,
      addGroupMember,
      removeGroupMember,
      saveGroup,
      removeGroup,
      phones,
      stats,
      alerts,
      history,
      historyDesc,
      search,
      filteredPhones,
      lowBalancePhones,
      dialogVisible,
      editingPhone,
      form,
      platformKeys,
      cfg,
      checkTimesText,
      remindDaysText,
      robotsList,
      robotsLoading,
      dingtalkReady,
      dingtalkHint,
      threshold,
      alertLabel,
      runCheck,
      sendTest,
      saveCfg,
      loadHistory,
      openEdit,
      save,
      remove,
    }
  },
}
</script>

<style scoped>
.phone-monitor {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.phone-sub-tabs :deep(.el-tabs__header) {
  margin-bottom: 14px;
}
.kpi-row {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}
.kpi-card {
  background: #fff;
  border: 1px solid #eef0f3;
  border-radius: 10px;
  padding: 14px 16px;
}
.kpi-label {
  font-size: 12px;
  color: #8a9199;
  margin-bottom: 6px;
}
.kpi-value {
  font-size: 22px;
  font-weight: 700;
  color: #1f2329;
}
.kpi-value.accent { color: #1677ff; }
.kpi-value.warn { color: #fa8c16; }
.kpi-value.danger { color: #f5222d; }
.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.text-danger { color: #f5222d; font-weight: 600; }
.empty-tip {
  text-align: center;
  color: #8a9199;
  padding: 28px 12px;
}
.alert-item {
  border-left: 3px solid #1677ff;
  background: #f7f8fa;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 8px;
}
.alert-item.debt,
.alert-item.balance_low,
.alert-item.daily_low { border-left-color: #f5222d; }
.alert-item.insufficient { border-left-color: #fa541c; }
.alert-item.deduction_day,
.alert-item.deduction_soon { border-left-color: #fa8c16; }
.alert-type {
  font-size: 12px;
  color: #8a9199;
  margin-bottom: 4px;
}
.alert-item pre {
  margin: 0;
  white-space: pre-wrap;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.5;
}
.cfg-hint {
  margin-left: 12px;
  color: #8a9199;
  font-size: 12px;
}
.bal-hint {
  margin-left: 4px;
  font-size: 11px;
  color: #8a9199;
}
.plat-cell {
  word-break: break-all;
}
.member-chip {
  display: inline-block;
  margin: 2px 6px 2px 0;
  padding: 2px 8px;
  background: #f0f2f5;
  border-radius: 4px;
  font-size: 12px;
}
.member-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
@media (max-width: 1200px) {
  .kpi-row { grid-template-columns: repeat(3, 1fr); }
}
@media (max-width: 800px) {
  .kpi-row { grid-template-columns: repeat(2, 1fr); }
}
</style>
